import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"

import { cn } from '@/shared/lib'
import { Button } from "@/shared/ui/button"
import { XIcon } from "lucide-react"

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 isolate z-50 bg-overlay/10 duration-100 supports-backdrop-filter:backdrop-blur-xs anim:data-open:animate-in data-open:fade-in-0 anim:data-closed:animate-out data-closed:fade-out-0",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  container,
  overlayClassName,
  forceOverlay = false,
  ...props
}: DialogPrimitive.Popup.Props & {
  container?: HTMLElement | ShadowRoot | null
  overlayClassName?: string
  /** Render the backdrop even when nested in another dialog (Base UI hides it otherwise). */
  forceOverlay?: boolean
}) {
  return (
    <DialogPortal container={container}>
      <DialogOverlay className={overlayClassName} forceRender={forceOverlay} />
      <DialogPrimitive.Popup
        aria-modal="true"
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm anim:data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 anim:data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          className
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

/**
 * The band (bled to the popup's edges, like `DialogFooter`'s own) holds only
 * the title and the close button — a proper flex sibling, not floated over
 * the whole popup with `position: absolute`, which let it sit on top of the
 * description below. Anything after the title (the description) renders as a
 * sibling of the band instead, in the plain content area, not inside it.
 */
function DialogHeader({
  className,
  showCloseButton = true,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  const [title, ...rest] = React.Children.toArray(children)
  return (
    <>
      <div
        data-slot="dialog-header"
        className={cn(
          "-mx-4 -mt-4 flex h-11 items-center justify-between gap-2 rounded-t-xl border-b bg-muted/50 pl-4",
          className
        )}
        {...props}
      >
        <div className="min-w-0 flex-1">{title}</div>
        {showCloseButton && (
          <DialogPrimitive.Close
            aria-label="Close"
            data-slot="dialog-close"
            // Flush against the header's own right edge (no padding to stop
            // there), so its top-right corner must match the popup's own
            // `rounded-xl` — otherwise its hover background squares off what
            // should be the popup's rounded corner, covering its border there.
            render={<Button variant="ghost" size="icon-sm" className="rounded-tr-xl border-transparent" />}
          >
            <XIcon aria-hidden="true" />
          </DialogPrimitive.Close>
        )}
      </div>
      {rest}
    </>
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "font-heading text-base leading-none font-medium",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
}
